import React, { useEffect, useState } from 'react';
import { sum } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useModalContext } from '../../../context/Modal';
import { FooterNavigator } from '../../Footer/FooterNavigator';
import { FormikProvider, useFormik } from 'formik';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useThemeContext } from '../../../context/Theme';
import Slider, { WithTooltip } from './Slider';
import { SET_STRATEGY_RISK_WEIGHTS } from '../../../http/apollo-queries';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { IStrategy } from '../../../types/model';
import { useUserStrategies } from '../../../context/UserStrategies';

const AdvancedSettings: React.FC<{ strategy: IStrategy }> = ({ strategy }) => {
  // Hooks
  const { t } = useTranslation();
  const { closeModal } = useModalContext();
  const { theme: globalTheme } = useThemeContext();
  const { refetch, loading: strategiesLoading } = useUserStrategies();

  // Derived data
  const {
    riskAssetConcentrationWeight,
    riskCurrencyConcentrationWeight,
    riskGeographicalConcentrationWeight,
    riskIndustryConcentrationWeight,
  } = strategy;

  const weights = [
    {
      title: t('widget.riskRate.assetConcentration'),
      formikName: 'riskAssetConcentrationWeight',
      initialValue: riskAssetConcentrationWeight,
    },
    {
      title: t('widget.riskRate.geographicalConcentration'),
      formikName: 'riskGeographicalConcentrationWeight',
      initialValue: riskGeographicalConcentrationWeight,
    },
    {
      title: t('widget.riskRate.currencyRisk'),
      formikName: 'riskCurrencyConcentrationWeight',
      initialValue: riskCurrencyConcentrationWeight,
    },
    {
      title: t('widget.riskRate.industryRisk'),
      formikName: 'riskIndustryConcentrationWeight',
      initialValue: riskIndustryConcentrationWeight,
    },
  ];

  // State
  const [lastTouched, setLastTouched] = useState<null | string>(null);
  const [isNot100Percent, setIsNot100Percent] = useState(false);

  // Form state
  const formik = useFormik({
    onSubmit: () => {},
    initialValues: weights.reduce(
      (acc, { initialValue, formikName }) => ({
        ...acc,
        [formikName]: initialValue,
      }),
      {}
    ),
  });

  // Mutation
  const [updateWeights, { loading }] = useMutation(SET_STRATEGY_RISK_WEIGHTS);

  function onSubmit() {
    updateWeights({
      variables: { ...formik.values, strategyId: strategy.id },
    })
      .then(() => {
        return refetch();
      })
      .then(() => {
        closeModal();
      })
      .catch(() => toast.error(''));
  }

  const sliderTheme = createTheme({
    palette: {
      mode: globalTheme.toLocaleLowerCase() as 'light' | 'dark',
    },
  });

  // Effects
  useEffect(() => {
    if (sum(Object.values(formik.values)) !== 100) {
      setIsNot100Percent(true);
    } else {
      setIsNot100Percent(false);
    }
  }, [formik.values]);

  return (
    <div className={'flex flex-col h-full w-full justify-between'}>
      <div className={'px-18 mt-5 overflow-y-auto h-full'}>
        <h2 className={'font-medium'}>{t('widget.riskRate.modal.subTitle')}</h2>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <div className="my-8">
              <ThemeProvider theme={sliderTheme}>
                {weights.map(({ title, formikName, initialValue }) => {
                  return (
                    <Slider
                      key={formikName}
                      name={formikName}
                      label={title}
                      formik={formik}
                      initialWeight={initialValue}
                      brokePercentage={
                        formikName === lastTouched && isNot100Percent
                      }
                      not100Percent={isNot100Percent}
                      formTouched={lastTouched !== null}
                      onChange={(name) => setLastTouched(name)}
                    />
                  );
                })}
              </ThemeProvider>
            </div>
          </form>
        </FormikProvider>
      </div>
      <FooterNavigator
        loading={loading || strategiesLoading}
        isDisabledSaveButton={isNot100Percent || loading || strategiesLoading}
        className={`
          border-t
          left-0
          right-0
          bottom-0
          rounded-b-xl
          border-grey-10
          bg-white
          flex-grow-0
          flex-shrink-0
          justify-between
        `}
        onCancel={closeModal}
        onSave={onSubmit}
      />
    </div>
  );
};

export default AdvancedSettings;
